import { useEffect, useState } from 'react'
import { Box, Stack, Typography, Button, Skeleton } from '@mui/material'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { PANEL_RADIUS } from '../../theme/tokens'

/**
 * PdfInlineFrame — the ONE place a PDF is mounted inline in this app.
 *
 * @description
 *   Probes `/files/<path>` with a HEAD request, then renders one of five
 *   honest states: a skeleton while probing, the native PDF `<iframe>` when
 *   the backend confirms it will serve inline, a "downloads instead" panel
 *   when the backend answers `Content-Disposition: attachment`, a terminal
 *   "no longer available" panel on 404/410, and "preview unavailable" with an
 *   "Open file" escape hatch on 401 / 5xx / network failure — a second attempt
 *   at the same URL, which can legitimately succeed after a transient failure
 *   but is NOT offered on 404/410, where it would lead into the same dead end.
 *   A stable visually-hidden `role="status"` region announces every
 *   transition; all FIVE subtrees swap wholesale, so without it the change is
 *   silent for assistive tech.
 *
 *   THE SUCCESS STATE CARRIES A DOWNLOAD LINK TOO, above the frame. Rendering
 *   the PDF is not the same as giving the user the file: Chromium's viewer has
 *   its own download button, but that button is browser chrome, not part of
 *   this document — so the successful state used to be the ONLY one of the five
 *   with no reachable way to obtain the file, while every failure state offered
 *   one. It is the same `DownloadPdfAction` the `attachment` panel renders, not
 *   a second affordance, and it sits above the frame because the drawer clips
 *   anything below it (see the comment at the `PDF_INLINE` branch for both
 *   reasons).
 *
 *   ⚠️ ON THE PHRASE "EMPTY ACCESSIBILITY TREE", which several documents around
 *   this change state as measured fact: the only instrument behind it is a
 *   Playwright a11y snapshot, and
 *   `docs/knowledge/operations/pdf-inline-browser-coverage.md` shows that same
 *   snapshot returning byte-identical output for a rendered PDF and for
 *   `about:blank`. It therefore cannot tell "the tree is empty" from "this
 *   instrument sees nothing here" (Rule 17 §2). The DECISION does not rest on
 *   it: the viewer's controls are outside this document either way, so nothing
 *   in the page's own tab order or a11y tree reaches the file, and Firefox and
 *   Safari are unmeasured entirely. Treat the emptiness as unverified and the
 *   conclusion as sound on the weaker premise.
 *
 *   THE PROBE NEVER BLOCKS THE SURFACE FOR MORE THAN `PROBE_TIMEOUT_MS`.
 *   `fetch` has no timeout of its own, and the skeleton is the only state
 *   with no way out. On timeout the frame mounts anyway — "we could not
 *   check" is not "the file is broken".
 *
 *   ONE WINDOW THE PROBE DOES NOT CLOSE, and the only case where it is
 *   confidently wrong: for up to `max_age` (1 h) after the deploy that turned
 *   PDFs inline, a client that had already downloaded a given PDF holds a
 *   cached `attachment` response. The probe goes to the network, correctly
 *   reads `inline`, and mounts the frame — which is then served that stale
 *   cache entry, so the download fires and the frame stays empty with no GET
 *   in the server log. Bounded, self-healing, only for previously-opened
 *   PDFs, no worse than the pre-probe behaviour. Full account and the reason
 *   it is not "fixed": docs/features/media-library.md § Cache caveat.
 *
 *   WHY A PROBE AND NOT `onError`: an `<iframe>` fires no `error` event for
 *   an HTTP error — it renders the error body (or nothing) and reports
 *   `load`. In the measured failure case the console was completely empty.
 *   Without an out-of-band request the app CANNOT observe that the frame
 *   stayed blank, which is why the image branch's `onError` pattern has no
 *   equivalent here. A HEAD costs no body, is same-origin, and needs no
 *   token (`/files/` is outside the `/api/*` JWT gate), and it answers all
 *   three questions at once: did it resolve, will it render, or will it
 *   download. `serve_file` declares `methods=["GET"]` and still answers the
 *   HEAD — Werkzeug adds it to any GET rule and strips the body; verified
 *   2026-08-05 against the real route shape (200, full headers, 0 bytes),
 *   not assumed from the docs.
 *
 *   NO `sandbox` ATTRIBUTE — deliberate, measured, and load-bearing.
 *   Measured 2026-08-05 in Playwright-Chromium with real PDF bytes: the
 *   iframe `sandbox` ATTRIBUTE switches Chromium's built-in PDF viewer off
 *   and leaves a grey rectangle, with ANY token set, with or without
 *   `allow-same-origin`. The response header
 *   `Content-Security-Policy: sandbox …` (`files_routes.PDF_SANDBOX_CSP`)
 *   does not: the viewer still renders under it. So the header is kept and
 *   the attribute is gone. That is not a weakening: the CSP directive
 *   sandboxes the DOCUMENT itself — opaque origin (no reach into the SPA's
 *   localStorage token), no `allow-popups`, no `allow-top-navigation`
 *   (which is what closes a PDF's own `/OpenAction /URI`) — and it applies
 *   on every path to the file, including the `/m/<short_id>` short link
 *   and a plain new tab, neither of which a frame attribute can reach.
 *   A second consequence, on purpose: there is no longer a second token set
 *   that has to stay in step with the header, so the drift risk that used
 *   to live in these files is gone rather than merely documented.
 *
 * @useWhen
 *   - Any surface that wants to show a PDF inside the app
 *     (MediaPreview full mode, MediaLightbox)
 *
 * @dontUseFor
 *   - MOBILE (`useMediaQuery(theme.breakpoints.down('sm'))`). iOS Safari and
 *     several Android browsers render only the first page of a framed PDF,
 *     or nothing at all, and the frame cannot detect that. Offer a plain
 *     "Open PDF" link instead — see `MediaDetailDrawer.jsx` `mobilePdf`.
 *     This component does NOT gate itself on viewport: the caller owns
 *     layout, and a silent self-disable would be harder to find than this
 *     line.
 *   - Cross-origin PDF URLs. The probe is skipped for anything that is not
 *     a root-relative path (a cross-origin HEAD would fail CORS and read as
 *     a false error), so those mount unprobed and keep today's behaviour.
 *   - Compact grid tiles — never mount a full file in a list.
 *
 * @related
 *   - MediaPreview / MediaLightbox — the two surfaces that mount it
 *   - backend/files_routes.py — `PDF_SANDBOX_CSP`, the three-part inline
 *     check, and the reason a PDF can legitimately answer `attachment`
 *
 * @reuseHints
 *   - Vendor invoice / PO document preview panels
 *   - Any "show me the document without leaving the page" surface
 *
 * @props
 *   url        — same-origin `/files/<path>` (or an absolute URL, unprobed)
 *   title      — iframe title / accessible name
 *   frameSx    — sx for the iframe and the skeleton that stands in for it
 *   fallbackSx — sx for the download / error panel
 */

export const PDF_PROBING = 'probing'
export const PDF_INLINE = 'inline'
export const PDF_DOWNLOAD = 'download'
export const PDF_MISSING = 'missing'
export const PDF_ERROR = 'error'

/**
 * How long the probe may hold the surface before we give up ON THE PROBE.
 *
 * `fetch` has no timeout of its own: against a server that accepts the socket
 * and never answers, it stays pending indefinitely (measured — still pending
 * after 20s). Without this the user gets a skeleton forever, and the skeleton
 * is the ONE state with no way out — no button, no file, nothing to click.
 * That is the grey-rectangle finding again, only friendlier.
 */
export const PROBE_TIMEOUT_MS = 4000

/** Root-relative only — `//host/x` is protocol-relative, i.e. cross-origin. */
export function isSameOriginPath(url) {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')
}

/**
 * Turn a HEAD response into one of the three terminal states.
 *
 * `attachment` is the interesting one: the backend's inline check (guessed
 * type + no encoding + `%PDF-` at offset 0) is stricter than the frontend's
 * (`mime_type === 'application/pdf'` on the DB row), so a row the UI calls a
 * PDF can legitimately come back as a download — a caller-supplied
 * `mime_type` in `file_storage.py`, or a BOM before `%PDF-`. Reading the
 * answer instead of assuming it is what keeps the frame from going blank
 * while a download starts unannounced.
 *
 * 404/410 get their own state because the OFFER differs, not the wording: for
 * a 5xx or a network blip "Open file" is a reasonable second try, but for a
 * file that is gone it is a button that leads to the same 404. An invitation
 * into a dead end is worse than no invitation.
 */
export function classifyPdfResponse(res) {
  if (!res) return PDF_ERROR
  if (!res.ok) return (res.status === 404 || res.status === 410) ? PDF_MISSING : PDF_ERROR
  const get = (h) => (res.headers && typeof res.headers.get === 'function' ? res.headers.get(h) : null)
  const disposition = (get('content-disposition') || '').trim().toLowerCase()
  if (disposition.startsWith('attachment')) return PDF_DOWNLOAD
  const contentType = (get('content-type') || '').split(';')[0].trim().toLowerCase()
  if (contentType && contentType !== 'application/pdf') return PDF_DOWNLOAD
  return PDF_INLINE
}

/** HEAD-probe `url`; see `classifyPdfResponse` for the state vocabulary. */
export function usePdfInlineProbe(url) {
  const [state, setState] = useState(() => (isSameOriginPath(url) ? PDF_PROBING : PDF_INLINE))

  useEffect(() => {
    // Cross-origin, missing, or no fetch at all (jsdom): degrade to today's
    // behaviour — mount the frame — rather than invent a failure we did not
    // observe.
    if (!isSameOriginPath(url) || typeof fetch !== 'function') {
      setState(url ? PDF_INLINE : PDF_ERROR)
      return undefined
    }
    let cancelled = false
    let timedOut = false
    const controller = typeof AbortController === 'function' ? new AbortController() : null
    setState(PDF_PROBING)

    // On timeout we fall to PDF_INLINE, NOT to PDF_ERROR. "We could not check"
    // is not "the file is broken" — same rule the cross-origin and no-`fetch`
    // branches above already follow, and the reason they do: inventing a
    // failure we never observed is how a silent guess becomes a wrong answer.
    // Mounting the frame is what would have happened without a probe at all,
    // so a slow network degrades to the old behaviour instead of to a lie.
    const timer = setTimeout(() => {
      timedOut = true
      if (controller) controller.abort()
      if (!cancelled) setState(PDF_INLINE)
    }, PROBE_TIMEOUT_MS)

    fetch(url, { method: 'HEAD', credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then((res) => { if (!cancelled && !timedOut) setState(classifyPdfResponse(res)) })
      .catch(() => { if (!cancelled && !timedOut) setState(PDF_ERROR) })
      .finally(() => { clearTimeout(timer) })

    return () => { cancelled = true; clearTimeout(timer); if (controller) controller.abort() }
  }, [url])

  return state
}

/** Screen-reader-only, without pulling in a new dependency. */
const SR_ONLY = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}

/**
 * What the live region says per state. The failure wordings match
 * `MediaPreview`'s image branch on purpose — same message, same weight.
 */
const STATE_MESSAGE = {
  [PDF_PROBING]: 'Loading PDF preview',
  // The success message NAMES the download link. The viewer itself is browser
  // chrome with an empty accessibility tree, so "PDF preview loaded" on its own
  // announces a success the user cannot then act on or even inspect. Pointing
  // at the one reachable control turns the announcement into an instruction.
  [PDF_INLINE]: 'PDF preview loaded. A Download PDF link is available before the preview.',
  [PDF_DOWNLOAD]: "This PDF can't be shown here — it opens as a download",
  [PDF_MISSING]: 'This PDF is no longer available',
  [PDF_ERROR]: 'Preview unavailable',
}

/**
 * Failure/instruction panel.
 *
 * Deliberately mirrors `MediaPreview`'s internal `Tile` (`action.hover` on
 * `text.disabled`, caption-sized text) rather than inventing a second
 * emphasis: the identical string "Preview unavailable" renders on the same
 * drawer from the image branch, and two weights for one message reads as two
 * different severities. `Tile` is not exported, and exporting it would make
 * MediaPreview ↔ PdfInlineFrame a circular import, so this matches its tokens
 * instead of importing it — the one thing to keep in step if `Tile` changes.
 *
 * `emphasis` is the single exception: the download panel is an INSTRUCTION
 * with an action, not a failure, so it stays at readable body weight.
 *
 * 🔴 The hierarchy rides on `variant` ONLY, never on colour. Both weights sit
 * at `text.secondary`. The earlier `text.disabled` for the non-emphasis case
 * matched `Tile` but computed to 2.65:1 on `action.hover` in light mode (MUI
 * 7.3.5 defaults, alpha-composited) — under WCAG AA. `text.secondary` is
 * 5.59:1. So this deliberately stops matching `Tile` on that ONE token, and
 * matches `MediaLightbox`'s own image-failure panel instead, which already
 * renders the identical string "Preview unavailable" at body2 +
 * `text.secondary`. If `Tile` is ever fixed the same way, they re-converge.
 */
function FallbackPanel({ icon, headline, action, fallbackSx, emphasis = false }) {
  return (
    <Stack
      spacing={1.5}
      alignItems="center"
      justifyContent="center"
      sx={{
        width: '100%',
        minHeight: 160,
        p: 3,
        bgcolor: 'action.hover',
        color: 'text.disabled',
        borderRadius: PANEL_RADIUS,
        ...fallbackSx,
      }}
    >
      {icon}
      {/* Weight (`variant`) carries the hierarchy; COLOUR does not. `text.disabled`
          on `action.hover` computes to 2.65:1 in light mode (MUI 7.3.5 defaults,
          alpha-composited) — below WCAG AA 4.5:1. `text.secondary` is 5.59:1 and
          keeps the instruction-vs-failure distinction on the axis that does not
          cost legibility. `MediaLightbox` already renders the identical string
          "Preview unavailable" at body2 + text.secondary; this matches it. */}
      <Typography
        variant={emphasis ? 'body2' : 'caption'}
        sx={{ color: 'text.secondary', textAlign: 'center' }}
      >
        {headline}
      </Typography>
      {action}
    </Stack>
  )
}

/**
 * The download control — ONE definition, rendered in two places.
 *
 * It is the same element in the `attachment` panel and above the mounted
 * viewer, on purpose: the point of putting it next to the frame is that the
 * user who cannot get the file OUT of the frame gets the identical affordance
 * they would have got had the frame refused to mount. Two hand-rolled copies
 * would drift, and the drift would be invisible because only one of the two
 * states is on screen at a time.
 *
 * Why it is not a new shared component: it is a `Button` with an `href`, local
 * to this file exactly like `FallbackPanel` is. Nothing outside this file needs
 * it, and exporting it would invite a third caller to mount a PDF affordance
 * without the probe behind it.
 *
 * 🔴 `download` IS LOAD-BEARING, and only in the state this control was added
 * for. `PDF_INLINE` is reached exactly when the HEAD disposition does NOT say
 * `attachment` (`classifyPdfResponse`), and `files_routes.py:239` sets
 * `as_attachment=not serve_inline` — so a verified PDF arrives `inline`. Without
 * this attribute the anchor is a same-tab NAVIGATION: the SPA unloads and the
 * user lands full-page in the very same viewer this control exists to escape.
 * Same appearance, opposite outcome. It is a no-op in the `attachment` panel,
 * where the header already forces the save (and `files_routes.py:240` supplies
 * the `filename`, which outranks a bare `download`, so the saved name does not
 * change either) — which is why one definition can still serve both without
 * the two drifting.
 *
 * ⚠️ `download` is same-origin only, by spec. Every `/media` path reaches this
 * component through `MediaPreview.fileUrl`, which returns a root-relative
 * `/files/<path>` for an `internal_file` — so on the surfaces that mount it
 * today the attribute always applies. A cross-origin URL would silently fall
 * back to navigation; those also skip the probe entirely and mount unprobed,
 * so they were never covered by this control's guarantees. Not observed in the
 * wild — whether such a row exists is a database question, not a code one.
 */
function DownloadPdfAction({ url }) {
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<FileDownloadOutlinedIcon />}
      component="a"
      href={url}
      download
    >
      Download PDF
    </Button>
  )
}

export default function PdfInlineFrame({ url, title, frameSx, fallbackSx }) {
  const state = usePdfInlineProbe(url)

  let content
  if (state === PDF_PROBING) {
    // aria-hidden: the live region below is what announces this state. A
    // Skeleton that also carried role="status" would be a second, competing
    // announcement — and an empty one, since it has no text.
    content = (
      <Skeleton
        variant="rectangular"
        aria-hidden="true"
        sx={{ borderRadius: PANEL_RADIUS, ...frameSx }}
      />
    )
  } else if (state === PDF_DOWNLOAD) {
    content = (
      <FallbackPanel
        emphasis
        fallbackSx={fallbackSx}
        icon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        headline="This PDF can't be shown here — it opens as a download."
        action={<DownloadPdfAction url={url} />}
      />
    )
  } else if (state === PDF_MISSING) {
    // No action on purpose: "Open file" here would lead to the same 404.
    content = (
      <FallbackPanel
        fallbackSx={fallbackSx}
        icon={<ReportProblemOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        headline="This PDF is no longer available."
      />
    )
  } else if (state === PDF_ERROR) {
    content = (
      <FallbackPanel
        fallbackSx={fallbackSx}
        icon={<ReportProblemOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        headline="Preview unavailable"
        action={url ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open file
          </Button>
        ) : null}
      />
    )
  } else {
    // PDF_INLINE. No `sandbox` attribute — see the header comment.
    //
    // THE DOWNLOAD LINE SITS ABOVE THE FRAME, and both facts about that
    // placement are load-bearing:
    //
    // 1. ABOVE, not below, because `MediaDetailDrawer` wraps this in a box with
    //    `maxHeight: 240` AND `overflow: 'hidden'` (`:113-114`) while `frameSx`
    //    asks for `height: 100%, minHeight: 240`. A sibling placed after the
    //    iframe lands outside the cap and is CLIPPED — present in the DOM,
    //    invisible on screen, and therefore exactly the kind of affordance that
    //    passes a test and helps nobody. Above the frame it is always in view.
    //
    //    What it COSTS, stated correctly because an earlier revision of this
    //    comment had it wrong in both magnitude and mechanism ("~24px off the
    //    bottom of the preview", as if the frame shrank): nothing shrinks. The
    //    Stack has no definite height and `frameSx` carries `minHeight: 240`,
    //    so the iframe still occupies its full 240 and the drawer's cap CLIPS
    //    roughly 39px off the BOTTOM of it (a ~30.75px small Button plus the
    //    8px Stack gap) — nearer 52px below `sm`, where `theme.js` floors
    //    buttons at 44. That is a trade taken deliberately: the bottom edge of
    //    a PDF's first page is worth less than the only way to obtain the file.
    //    Not verified in a browser — jsdom has no layout engine.
    // 2. For a screen reader it also lands BEFORE the dead end rather than
    //    after it. The PDF viewer is browser chrome and its accessibility tree
    //    comes back EMPTY, so a user who tabs into the frame has nothing to
    //    read and no way to reach the file; meeting the escape hatch first is
    //    the difference between an alternative and a consolation prize.
    //
    // The frame keeps `frameSx` verbatim — no caller had to change its layout
    // for this, which is why it is a `Stack` and not a rewrite of the box model.
    content = (
      <Stack spacing={1} sx={{ width: '100%', alignItems: 'flex-start' }}>
        <DownloadPdfAction url={url} />
        <Box
          component="iframe"
          title={title || 'PDF preview'}
          src={url}
          sx={{ border: 'none', borderRadius: PANEL_RADIUS, ...frameSx }}
        />
      </Stack>
    )
  }

  // The live region is a STABLE node that gets new text — it is rendered from
  // the first paint and never unmounted between states. A region inserted at
  // the moment of the change typically announces nothing; and the five states
  // swap whole subtrees, so without it the transition is silent for assistive
  // tech. `aria-busy` carries the probing state on the same node.
  return (
    <>
      <Box
        component="span"
        role="status"
        aria-live="polite"
        aria-busy={state === PDF_PROBING}
        sx={SR_ONLY}
      >
        {STATE_MESSAGE[state]}
      </Box>
      {content}
    </>
  )
}
